import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  AppBar,
  Toolbar,
  InputAdornment,
  CircularProgress,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';

const Zonas = () => {
  const [zonas, setZonas] = useState([]);
  const [originalZonas, setOriginalZonas] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedZona, setSelectedZona] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    nombre_zona: '',
    activo: true,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const navigate = useNavigate();

  useEffect(() => {
    fetchZonas();
  }, []);

  // Lógica de búsqueda/filtrado (Igual que en Marcas)
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setZonas(originalZonas);
    } else {
      const filtered = originalZonas.filter((zona) =>
        zona.nombre_zona.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setZonas(filtered);
    }
  }, [searchTerm, originalZonas]);

  const fetchZonas = async () => {
    try {
      setLoading(true);
      const response = await api.get('/zonas/');
      setZonas(response.data);
      setOriginalZonas(response.data);
      setError(null);
    } catch (err) {
      console.error("Error al cargar zonas:", err);
      setError('No se pudieron cargar las zonas');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (zona = null) => {
    if (zona) {
      setSelectedZona(zona);
      setFormData({
        nombre_zona: zona.nombre_zona,
        activo: zona.activo,
      });
    } else {
      setSelectedZona(null);
      setFormData({
        nombre_zona: '',
        activo: true,
      });
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedZona(null);
  };

  const handleSave = async () => {
    try {
      if (selectedZona) {
        await api.put(`/zonas/${selectedZona.id_zona}`, formData);
        setSnackbarMessage('Zona actualizada correctamente');
      } else {
        await api.post('/zonas/', formData);
        setSnackbarMessage('Zona creada correctamente');
      }
      setSnackbarOpen(true);
      handleCloseModal();
      fetchZonas();
    } catch (err) {
      setSnackbarMessage(err.response?.data?.detail || 'Error al guardar la zona');
      setSnackbarOpen(true);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar esta zona?')) {
      try {
        await api.delete(`/zonas/${id}`);
        setSnackbarMessage('Zona eliminada correctamente');
        setSnackbarOpen(true);
        fetchZonas();
      } catch (err) {
        setSnackbarMessage('Error al eliminar la zona');
        setSnackbarOpen(true);
      }
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString('es-ES');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f4f6f8' }}>
      <AppBar position="static" sx={{ bgcolor: 'white', color: 'text.primary', boxShadow: 1 }}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main', mr: 3 }}>
              Gestión de Zonas
            </Typography>
            <Button
              variant="contained"
              color="success"
              startIcon={<AddIcon />}
              onClick={() => handleOpenModal()}
            >
              Nueva Zona
            </Button>
          </Box>
          <TextField
            size="small"
            placeholder="Buscar por nombre..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ width: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, flexGrow: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 3 }}>
            <Table>
              <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Nombre de Zona</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Estado</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Fecha de Creación</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {zonas.length > 0 ? (
                  zonas.map((zona) => (
                    <TableRow key={zona.id_zona} hover>
                      <TableCell>{zona.nombre_zona}</TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: 'inline-block',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            bgcolor: zona.activo ? '#e8f5e9' : '#ffebee',
                            color: zona.activo ? '#2e7d32' : '#c62828',
                            fontWeight: 'bold',
                            fontSize: '0.75rem',
                          }}
                        >
                          {zona.activo ? 'ACTIVA' : 'INACTIVA'}
                        </Box>
                      </TableCell>
                      <TableCell>{formatDate(zona.creado_en)}</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <IconButton color="primary" onClick={() => handleOpenModal(zona)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton color="error" onClick={() => handleDelete(zona.id_zona)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                      No se encontraron resultados
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Dialog open={openModal} onClose={handleCloseModal} fullWidth maxWidth="xs">
          <DialogTitle sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
            {selectedZona ? 'Editar Zona' : 'Nueva Zona'}
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Nombre de la Zona"
              value={formData.nombre_zona}
              onChange={(e) => setFormData({ ...formData, nombre_zona: e.target.value })}
              sx={{ mb: 2, mt: 1 }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Switch
                checked={formData.activo}
                onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
              />
              <Typography>Zona Activa</Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseModal} color="inherit">Cancelar</Button>
            <Button 
              onClick={handleSave} 
              variant="contained" 
              disabled={!formData.nombre_zona.trim()}
            >
              {selectedZona ? 'Actualizar' : 'Guardar'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={handleSnackbarClose} severity={snackbarMessage.includes('Error') ? 'error' : 'success'}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default Zonas;